<template>
  <div class="q-gutter-y-lg relative-position">
    <div class="bg-primary q-px-md q-py-lg primary-pointer">
      <div class="row justify-center items-center text-white q-gutter-sm">
        <span class="text-title text-weight-medium">Your</span>
        <FrequencyDropDown
          :value="frequency"
          @input="$emit('update:frequency', $event)"
        />
        <BudgetViewDropdown
          :value="budgetComparisonView"
          @input="$emit('update:budget-comparison', $event)"
        />
        <span class="text-title text-weight-medium">
          compared to a car loan
        </span>
      </div>

      <div class="row q-mt-lg">
        <BudgetPayComparisonOption
          :loading="loading"
          :label="salarySacrificeEnum.label"
          :img-path="salarySacrificeEnum.imgPath"
          :active-img-path="salarySacrificeEnum.activeImgPath"
          :monetary-value="
            salarySacrificeValue | frequency('monthly', frequency)
          "
          :active="salarySacrificeEnum.value === budgetLeaseTypeView"
          @select="$emit('update:budget-lease-type', salarySacrificeEnum.value)"
        />
        <BudgetPayComparisonOption
          :loading="loading"
          :label="notSalarySacrificeEnum.label"
          :img-path="notSalarySacrificeEnum.imgPath"
          :active-img-path="notSalarySacrificeEnum.activeImgPath"
          :monetary-value="
            notSalarySacrificeValue | frequency('monthly', frequency)
          "
          :active="notSalarySacrificeEnum.value === budgetLeaseTypeView"
          @select="
            $emit('update:budget-lease-type', notSalarySacrificeEnum.value)
          "
        />
      </div>

      <div class="bg-white">
        <BudgetSacrificePaymentSummary
          :loading="loading"
          v-if="
            budgetComparisonView === paymentEnum.value &&
            budgetLeaseTypeView === salarySacrificeEnum.value
          "
          :lease-term-years="leaseTermYears"
          :novated-saving="novated.novatedSaving"
          :total-tax-saving="novated.totalTaxSaving"
          :tax-saving="novated.taxSaving"
          :gst-saving-budget="novated.gstSavingBudget"
          :gst-saving-vehicle="novated.gstSavingVehicle"
          :frequency="frequency"
        />
        <BudgetNotSacrificePaymentSummary
          v-if="
            budgetComparisonView === paymentEnum.value &&
            budgetLeaseTypeView === notSalarySacrificeEnum.value
          "
        />

        <BudgetSacrificeTakeHomeSummary
          :loading="loading"
          v-if="
            budgetComparisonView === takeHomeEnum.value &&
            budgetLeaseTypeView === salarySacrificeEnum.value
          "
          :impact="novated.salarySacrificeImpactTakeHome"
          :frequency="frequency"
        />

        <BudgetNotSacrificeTakeHomeSummary
          :loading="loading"
          v-if="
            budgetComparisonView === takeHomeEnum.value &&
            budgetLeaseTypeView === notSalarySacrificeEnum.value
          "
          :impact="novated.notSalarySacrificeImpactTakeHome"
          :frequency="frequency"
        />
      </div>

      <BudgetCalculationsSummary
        :sacrifice-active="salarySacrificeEnum.value === budgetLeaseTypeView"
        :no-sacrifice-active="
          notSalarySacrificeEnum.value === budgetLeaseTypeView
        "
        :loading="loading"
        :calculations="calculations"
        :frequency="frequency"
      />

      <BudgetTakeHomeSummary
        :sacrifice-active="salarySacrificeEnum.value === budgetLeaseTypeView"
        :no-sacrifice-active="
          notSalarySacrificeEnum.value === budgetLeaseTypeView
        "
        :loading="loading"
        :sacrifice="takeHome.sacrifice"
        :no-sacrifice="takeHome.noSacrifice"
        :frequency="frequency"
      />

      <div class="row q-col-gutter-x-md q-mt-md">
        <div class="col">
          <QBtn
            class="full-width"
            outline
            label="Print Summary"
            color="white"
            icon="eva-printer-outline"
            @click="print"
          />
        </div>
      </div>
    </div>

    <div class="row justify-center print-hide">
      <SAIBtn
        :disable="actionDisable || actionLoading || disable"
        :loading="actionLoading"
        :label="actionLabel"
        padding="12px xl"
        @click="$emit('continue')"
      />
    </div>
  </div>
</template>

<script>
import { SAIBtn } from 'ecx.saigon.ui.common';
import { QBtn } from 'quasar';

export default {
  name: 'BudgetSummary',
  props: {
    loading: Boolean,
    disable: Boolean,
    actionLabel: String,
    actionLoading: Boolean,
    actionDisable: Boolean,
    budgetComparisonView: String,
    budgetLeaseTypeView: String,
    leaseTermYears: Number,
    novated: {
      type: Object,
      default: () => ({}),
    },
    calculations: {
      type: Array,
      default: () => [],
    },
    takeHome: {
      type: Object,
      default: () => ({}),
    },
    frequency: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      paymentEnum: this.$Constants.BUDGET_PAY_COMPARISON_TYPES.payment,
      takeHomeEnum: this.$Constants.BUDGET_PAY_COMPARISON_TYPES.takeHomePay,
      salarySacrificeEnum:
        this.$Constants.BUDGET_LEASE_COMPARISON_TYPES.salarySacrifice,
      notSalarySacrificeEnum:
        this.$Constants.BUDGET_LEASE_COMPARISON_TYPES.notSalarySacrifice,
    };
  },
  computed: {
    salarySacrificeValue() {
      return this.budgetComparisonView === this.paymentEnum.value
        ? this.novated.salarySacrificePayment
        : this.novated.salarySacrificeTakeHomePay;
    },
    notSalarySacrificeValue() {
      return this.budgetComparisonView === this.paymentEnum.value
        ? this.novated.notSalarySacrificePayment
        : this.novated.notSalarySacrificeTakeHomePay;
    },
  },
  methods: {
    print() {
      window.print();
    },
  },
  mounted() {
    this.$el.scrollIntoView();
  },
  components: {
    SAIBtn,
    QBtn,
    FrequencyDropDown: () => import('../../atom/frequency-dropdown'),
    BudgetViewDropdown: () => import('../../atom/budget-view-dropdown'),
    BudgetPayComparisonOption: () =>
      import('../../molecule/budget-pay-comparison-option'),
    BudgetSacrificePaymentSummary: () =>
      import('../../molecule/budget-sacrifice-payment-summary'),
    BudgetNotSacrificePaymentSummary: () =>
      import('../../molecule/budget-not-sacrifice-payment-summary'),
    BudgetSacrificeTakeHomeSummary: () =>
      import('../../molecule/budget-sacrifice-take-home-summary'),
    BudgetNotSacrificeTakeHomeSummary: () =>
      import('../../molecule/budget-not-sacrifice-take-home-summary'),
    BudgetCalculationsSummary: () =>
      import('../../molecule/budget-calculations-summary'),
    BudgetTakeHomeSummary: () =>
      import('../../molecule/budget-take-home-summary'),
  },
};
</script>

<style lang="sass" scoped>
.primary-pointer::after
    content: ''
    position: absolute
    width: 16px
    height: 16px
    top: -8px
    left: 0
    right: 0
    margin: auto
    transform: rotate(45deg)
    background: $primary
</style>
